import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  IPreferencesMenu,
  IStateRequestUpdate,
  PreferencesActionEnum,
  PreferencesMenuItemTypeEnum,
  PreferencesMenuTypeEnum,
  TypeSelectNotificationOptionsEnum,
} from 'Data/interfaces/Preferences/IPreferences';
import {
  IReportEmailSettings,
  ReportPeriodicityEnum,
} from 'Data/interfaces/Preferences/IReport';
import {
  listActivitiesActions,
  listDisciplinesActions,
  listFoldersAndFilesActions,
  listPlottingActions,
  NotificationActionEnum,
} from 'Data/interfaces/Notification/INotification';
import {
  DisciplineNotificationPreferenceEnum,
  FileNotificationPreferenceEnum,
  IPreferences,
  PlottingNotificationPreferenceEnum,
  TopicNotificationPreferenceEnum,
} from 'Data/interfaces/Preferences/IPreferencesApi';
import { useQueryReportEmails } from 'Hooks/report/useQueryReportEmails';
import { useQueryPreferencesEmail } from 'Hooks/preferences/useQueryPreferencesEmail';
import { useQueryPreferences } from 'Hooks/preferences/useQueryPreferences';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import { IOptionData } from '../Select';

const useOptions = () => {
  const preferencesFeatureFlag = useFeatureFlag('preferences-feature');
  const preferencesFeature = preferencesFeatureFlag.enabled && preferencesFeatureFlag.value === 'test';

  const {
    data: reportEmailData,
    isFetching: isFetchingReportEmail,
  } = useQueryReportEmails({ enabled: preferencesFeature });
  const {
    data: preferencesNotificationsData,
    isFetching: isFetchingPreferencesNotifications,
  } = useQueryPreferences({
    enabled: preferencesFeature,
  });
  const {
    data: preferencesEmailData,
    isFetching: isFetchingPreferencesEmail,
  } = useQueryPreferencesEmail({
    enabled: preferencesFeature,
  });

  const firstObjOptions = useRef(JSON.parse(JSON.stringify(objDefaultOptions)));
  const [objOptions, setObjOptions] = useState({ ...objDefaultOptions });

  const arrayOptions = useMemo(() => {
    return Object.values(objOptions);
  }, [objOptions]);

  const statesIsEquals = useMemo(() => {
    return JSON.stringify(firstObjOptions.current) === JSON.stringify(objOptions);
  }, [objOptions]);

  const handleUpdate = useCallback(({
    Type,
    itemParentId,
    itemChildrenId,
    valueId,
    value,
    isLeft,
    selectType,
  }: IStateRequestUpdate) => {
    setObjOptions((prev) => {
      const itemParentIndex = prev[Type].Items.findIndex((item) => {
        return item.Id === itemParentId;
      });
      if (itemParentIndex !== -1) {
        const itemChildrenIndex = prev[Type].Items[itemParentIndex].Items
          .findIndex((itemChildren) => {
            return itemChildren.Id === itemChildrenId;
          });
        if (itemChildrenIndex !== -1) {
          const valueIndex = prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items
            .findIndex((itemValue) => {
              return itemValue.Id === valueId;
            });
          if (valueIndex !== -1) {
            const prevValue = prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex];
            if (typeof value === 'string' && 'ValueSelect' in prevValue) {
              prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueSelect = value;
            }
            if (typeof value === 'boolean') {
              if ('ValueSwitch' in prevValue) {
                if (
                  selectType &&
                  !prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueSwitch &&
                  value
                ) {
                  prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueSelect = selectType;
                }
                prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueSwitch = value;
              }
              if ('ValueLeft' in prevValue && isLeft) {
                prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueLeft = value;
              }
              if ('ValueRight' in prevValue && !isLeft) {
                prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueRight = value;
              }
            }
          }
        }
      }
      return { ...prev };
    });
  }, []);

  const handleOldData = useCallback(() => {
    setObjOptions(JSON.parse(JSON.stringify(firstObjOptions.current)));
  }, []);

  useEffect(() => {
    const handleFirstPopulated = (
      enabledPreferences?: boolean,
      syncReportEmailData?: IReportEmailSettings,
      enabledPreferencesEmail?: boolean,
      syncPreferencesEmailData?: IPreferences[],
      enabledPreferencesNotifications?: boolean,
      syncPreferencesNotificationsData?: IPreferences[],
    ) => {
      const newObjOptions = { ...objDefaultOptions };
      if (enabledPreferences) {
        newObjOptions[PreferencesMenuTypeEnum.ReportByEmail].IsDisabled = false;
      }
      if (syncReportEmailData) {
        newObjOptions[PreferencesMenuTypeEnum.ReportByEmail].Items.forEach((itemParent) => {
          itemParent.Items.forEach((item) => {
            item.Items.forEach((itemChildren) => {
              if (itemChildren.ParamSwitch && itemChildren.ParamSwitch in syncReportEmailData) {
                itemChildren.ValueSwitch = syncReportEmailData[itemChildren.ParamSwitch as keyof IReportEmailSettings] as boolean;
              }
              if (itemChildren.ParamSelect && itemChildren.ParamSelect in syncReportEmailData) {
                itemChildren.ValueSelect = ReportPeriodicityEnum[syncReportEmailData[itemChildren.ParamSelect as keyof IReportEmailSettings] as any].toString();
              }
            });
          });
        });
      }
      if (enabledPreferencesEmail) {
        newObjOptions[PreferencesMenuTypeEnum.NotificationByEmail].IsDisabled = false;
        newObjOptions[PreferencesMenuTypeEnum.NotificationByEmail].Items.forEach((itemParent) => {
          itemParent.Items.forEach((item) => {
            item.Items.forEach((itemChildren) => {
              itemChildren.ValueSwitchDisabled = false;
              if (syncPreferencesEmailData) {
                const preferenceEmailItem = syncPreferencesEmailData.find((item) => {
                  return item.Action === itemChildren.SelectType as number;
                });
                if (preferenceEmailItem) {
                  if (listFoldersAndFilesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceEmailItem.Preference === FileNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false
                    // itemChildren.ValueSelect = FileNotificationPreferenceEnum[preferenceEmailItem?.Preference]?.toString()
                  }
                  if (listActivitiesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceEmailItem.Preference === TopicNotificationPreferenceEnum.None ? false : true;
                    itemChildren.ValueSelectDisabled = false;
                    itemChildren.ValueSelect = TopicNotificationPreferenceEnum[preferenceEmailItem?.Preference]?.toString();
                  }
                  if (listPlottingActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceEmailItem.Preference === PlottingNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false;
                    // itemChildren.ValueSelect = PlottingNotificationPreferenceEnum[preferenceEmailItem?.Preference]?.toString();
                  }
                  if (listDisciplinesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceEmailItem.Preference === DisciplineNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false
                    // itemChildren.ValueSelect = DisciplineNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString()
                  }
                }
              }
            });
          });
        });
      }
      if (enabledPreferencesNotifications) {
        newObjOptions[PreferencesMenuTypeEnum.Notifications].IsDisabled = false;
        newObjOptions[PreferencesMenuTypeEnum.Notifications].Items.forEach((itemParent) => {
          itemParent.Items.forEach((item) => {
            item.Items.forEach((itemChildren) => {
              itemChildren.ValueSwitchDisabled = false;
              if (syncPreferencesNotificationsData) {
                const preferenceNotificationItem = syncPreferencesNotificationsData.find((item) => {
                  return item.Action === itemChildren.SelectType as number;
                });
                if (preferenceNotificationItem) {
                  if (listFoldersAndFilesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceNotificationItem.Preference === FileNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false
                    // itemChildren.ValueSelect = FileNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString()
                  }
                  if (listActivitiesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceNotificationItem.Preference === TopicNotificationPreferenceEnum.None ? false : true;
                    itemChildren.ValueSelectDisabled = false;
                    itemChildren.ValueSelect = TopicNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString();
                  }
                  if (listPlottingActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceNotificationItem.Preference === PlottingNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false;
                    // itemChildren.ValueSelect = PlottingNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString();
                  }
                  if (listDisciplinesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceNotificationItem.Preference === DisciplineNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false
                    // itemChildren.ValueSelect = DisciplineNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString()
                  }
                }
              }
            });
          });
        });
      }
      firstObjOptions.current = JSON.parse(JSON.stringify(newObjOptions));
      setObjOptions(newObjOptions);
    };

    if (
      reportEmailData && !isFetchingReportEmail &&
      preferencesEmailData && !isFetchingPreferencesEmail &&
      preferencesNotificationsData && !isFetchingPreferencesNotifications
    ) {
      handleFirstPopulated(
        preferencesFeature,
        reportEmailData,
        preferencesFeature,
        preferencesEmailData,
        preferencesFeature,
        preferencesNotificationsData,
      );
    }
  }, [
    preferencesFeature,
    reportEmailData,
    isFetchingReportEmail,
    preferencesEmailData,
    isFetchingPreferencesEmail,
    preferencesNotificationsData,
    isFetchingPreferencesNotifications,
  ]);

  return {
    hasReportEmailData: !!reportEmailData,
    preferencesEmailData,
    hasPreferencesEmailData: preferencesEmailData?.length > 0,
    preferencesNotificationsData,
    hasPreferencesNotificationsData: preferencesNotificationsData?.length > 0,
    listLoading: isFetchingReportEmail || isFetchingPreferencesEmail || isFetchingPreferencesNotifications,
    objOptions,
    arrayOptions,
    statesIsEquals,
    onStateUpdate: handleUpdate,
    onOldData: handleOldData,
  };
};

export default useOptions;

type TypeReportMenuObj = Record<PreferencesMenuTypeEnum, IPreferencesMenu>;

export const objDefaultOptions: TypeReportMenuObj = {
  [PreferencesMenuTypeEnum.Notifications]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.Notifications,
    Items: [
      {
        Id: uuid(),
        Title: 'Notificações da Central',
        Order: 1,
        Items: [
          {
            Id: uuid(),
            Title: 'Pastas e arquivos',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: 'Novo arquivo',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: 'Arquivo excluído',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: 'Arquivo movido',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileMoved,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: 'Arquivo movido para obsoleto',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileCreatedObsolet,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: 'Arquivo renomeado',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileRenamed,
              },
              {
                Id: uuid(),
                Order: 6,
                Title: 'Nova revisão de arquivo',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileVersionAdded,
              },
              {
                Id: uuid(),
                Order: 7,
                Title: 'Descrição de arquivo alterada',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileDescriptionUpdated,
              },
              {
                Id: uuid(),
                Order: 8,
                Title: 'Renderização de arquivo concluída',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileRendered,
              },
              {
                Id: uuid(),
                Order: 9,
                Title: 'Status de arquivo alterado',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileStatusChanged,
              },
              {
                Id: uuid(),
                Order: 10,
                Title: 'Pasta criada',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderCreated,
              },
              {
                Id: uuid(),
                Order: 11,
                Title: 'Pasta renomeada',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderRenamed,
              },
              {
                Id: uuid(),
                Order: 12,
                Title: 'Pasta excluída',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderDeleted,
              },
            ],
          },
          {
            Id: uuid(),
            Title: 'Atividades',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 2,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: 'Nova atividade',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: 'Atividade atualizada',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicUpdated,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: 'Status de atividade alterado',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicStatusChanged,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: 'Prazo de atividade vencido ontem',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiredYesterday,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: 'Prazo de atividade a vencer amanhã',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiresTommorow,
              },
              {
                Id: uuid(),
                Order: 6,
                Title: 'Prazo de atividade a vencer hoje',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiresToday,
              },
              {
                Id: uuid(),
                Order: 7,
                Title: 'Atividade excluída',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicDeleted,
              },
              {
                Id: uuid(),
                Order: 8,
                Title: 'Arquivo anexado à atividade',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicAttachmentAdded,
              },
              {
                Id: uuid(),
                Order: 9,
                Title: 'Arquivo excluído da atividade',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicAttachmentDeleted,
              },
              {
                Id: uuid(),
                Order: 10,
                Title: 'Novo Comentário',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicCommentAdded,
              },
            ],
          },
          {
            Id: uuid(),
            Title: 'Disciplinas',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 3,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: 'Disciplina criada',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: 'Disciplina excluída',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: 'Disciplina renomeada',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineRenamed,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: 'Disciplina desconectada',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineDisconnected,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: 'Disciplina reconectada',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineReconnected,
              },
            ],
          },
          {
            Id: uuid(),
            Title: 'Plotagens',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 4,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: 'Nova solicitação de plotagem',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: 'Plotagem excluída',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingFileDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: 'Solicitação de plotagem excluída',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingRequestDeleted,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: 'Status de plotagem alterado',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingStatusChanged,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: 'Plotagem obsoleta',
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingCreatedObsolet,
              },
            ],
          },
        ],
      },
    ],
  },
  [PreferencesMenuTypeEnum.NotificationByEmail]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.NotificationByEmail,
    Items: [
      {
        Id: uuid(),
        Title: 'E-mail de Notificação',
        Order: 1,
        Items: [
          {
            Id: uuid(),
            Title: 'Pastas e arquivos',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: 'Novo arquivo',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: 'Arquivo excluído',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: 'Arquivo movido',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileMoved,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: 'Arquivo movido para obsoleto',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileCreatedObsolet,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: 'Arquivo renomeado',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileRenamed,
              },
              {
                Id: uuid(),
                Order: 6,
                Title: 'Nova revisão de arquivo',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileVersionAdded,
              },
              {
                Id: uuid(),
                Order: 7,
                Title: 'Descrição de arquivo alterada',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileDescriptionUpdated,
              },
              {
                Id: uuid(),
                Order: 8,
                Title: 'Renderização de arquivo concluída',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileRendered,
              },
              {
                Id: uuid(),
                Order: 9,
                Title: 'Status de arquivo alterado',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileStatusChanged,
              },
              {
                Id: uuid(),
                Order: 10,
                Title: 'Pasta criada',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderCreated,
              },
              {
                Id: uuid(),
                Order: 11,
                Title: 'Pasta renomeada',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderRenamed,
              },
              {
                Id: uuid(),
                Order: 12,
                Title: 'Pasta excluída',
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderDeleted,
              },
            ],
          },
          {
            Id: uuid(),
            Title: 'Atividades',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 2,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: 'Nova atividade',
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.TopicCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: 'Atividade atualizada',
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.TopicUpdated,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: 'Status de atividade alterado',
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.TopicStatusChanged,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: 'Prazo de atividade vencido ontem',
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiredYesterday,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: 'Prazo de atividade a vencer amanhã',
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiresTommorow,
              },
              {
                Id: uuid(),
                Order: 6,
                Title: 'Prazo de atividade a vencer hoje',
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiresToday,
              },
              {
                Id: uuid(),
                Order: 7,
                Title: 'Atividade excluída',
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicDeleted,
              },
              {
                Id: uuid(),
                Order: 8,
                Title: 'Arquivo anexado à atividade',
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicAttachmentAdded,
              },
              {
                Id: uuid(),
                Order: 9,
                Title: 'Arquivo excluído da atividade',
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicAttachmentDeleted,
              },
              {
                Id: uuid(),
                Order: 10,
                Title: 'Novo Comentário',
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicCommentAdded,
              },
            ],
          },
          {
            Id: uuid(),
            Title: 'Disciplinas',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 3,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: 'Disciplina criada',
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.DisciplineCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: 'Disciplina excluída',
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.DisciplineDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: 'Disciplina renomeada',
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.DisciplineRenamed,
              },
              // {
              //   Id: uuid(),
              //   Order: 4,
              //   Title: 'Disciplina desconectada',
              //   ValueSelect: 'All',
              //   ValueSelectDisabled: true,
              //   ValueSwitch: false,
              //   ValueSwitchDisabled: true,
              //   SelectType: NotificationActionEnum.DisciplineDisconnected,
              // },
              // {
              //   Id: uuid(),
              //   Order: 5,
              //   Title: 'Disciplina reconectada',
              //   ValueSelect: 'All',
              //   ValueSelectDisabled: true,
              //   ValueSwitch: false,
              //   ValueSwitchDisabled: true,
              //   SelectType: NotificationActionEnum.DisciplineReconnected,
              // },
            ],
          },
          // {
          //   Id: uuid(),
          //   Title: 'Plotagens',
          //   Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
          //   Order: 4,
          //   Items: [
          //     {
          //       Id: uuid(),
          //       Order: 1,
          //       Title: 'Nova solicitação de plotagem',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingCreated,
          //     },
          //     {
          //       Id: uuid(),
          //       Order: 2,
          //       Title: 'Plotagem excluída',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingFileDeleted,
          //     },
          //     {
          //       Id: uuid(),
          //       Order: 3,
          //       Title: 'Solicitação de plotagem excluída',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingRequestDeleted,
          //     },
          //     {
          //       Id: uuid(),
          //       Order: 4,
          //       Title: 'Status de plotagem alterado',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingStatusChanged,
          //     },
          //     {
          //       Id: uuid(),
          //       Order: 5,
          //       Title: 'Plotagem obsoleta',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingCreatedObsolet,
          //     },
          //   ],
          // },
        ],
      },
    ],
  },
  [PreferencesMenuTypeEnum.ReportByEmail]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.ReportByEmail,
    Items: [
      {
        Id: uuid(),
        Title: 'Relatório de arquivos',
        Order: 1,
        Items: [
          {
            Id: uuid(),
            Title: '',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelect,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                TitleSwitch: 'Receber relatório de arquivo pelo e-mail',
                DescriptionSwitch: 'Receba um relatório com o resumo das ações dos arquivos.',
                ParamSwitch: 'ReceiveFileReportEmail',
                ParamRequestSwitch: 'receiveFileReportEmail',
                ValueSwitch: true,
                TitleSelect: 'Periodicidade do relatório de arquivos',
                DescriptionSelect: 'Defina a periodicidade que você deseja receber o e-mail de relatório de arquivos.',
                ParamSelect: 'FileReportPeriodicity',
                ParamRequestSelect: 'fileReportPeriodicity',
                ValueSelect: 'Weekly',
                SelectType: PreferencesActionEnum.Periodicity,
                isPeriodicity: true,
                Order: 1,
              },
            ],
          },
        ],
      },
      {
        Id: uuid(),
        Title: 'Relatório de atividades',
        Order: 2,
        Items: [
          {
            Id: uuid(),
            Title: '',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelect,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                TitleSwitch: 'Receber relatório de atividades pelo e-mail',
                DescriptionSwitch: 'Receba um relatório com o resumo das atividades.',
                ParamSwitch: 'ReceiveTopicReportEmail',
                ParamRequestSwitch: 'receiveTopicReportEmail',
                ValueSwitch: true,
                TitleSelect: 'Periodicidade do relatório de atividades',
                DescriptionSelect: 'Defina a periodicidade que você deseja receber o e-mail de relatório de atividades.',
                ParamSelect: 'TopicReportPeriodicity',
                ParamRequestSelect: 'topicReportPeriodicity',
                ValueSelect: 'Weekly',
                SelectType: PreferencesActionEnum.Periodicity,
                isPeriodicity: true,
                Order: 1,
              },
            ],
          },
        ],
      },
      {
        Id: uuid(),
        Title: 'Relatório de plotagens',
        Order: 3,
        Items: [
          {
            Id: uuid(),
            Title: '',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelect,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                TitleSwitch: 'Receber relatório de plotagens pelo e-mail',
                DescriptionSwitch: 'Receba um relatório com o resumo das plotagens.',
                ParamSwitch: 'ReceivePlottReportEmail',
                ParamRequestSwitch: 'receivePlottReportEmail',
                ValueSwitch: true,
                TitleSelect: 'Periodicidade do relatório de plotagens',
                DescriptionSelect: 'Defina a periodicidade que você deseja receber o e-mail de relatório de plotagens.',
                ParamSelect: 'PlottReportPeriodicity',
                ParamRequestSelect: 'plottReportPeriodicity',
                ValueSelect: 'Weekly',
                SelectType: PreferencesActionEnum.Periodicity,
                isPeriodicity: true,
                Order: 1,
              },
            ],
          },
        ],
      },
    ],
  },
  [PreferencesMenuTypeEnum.Language]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.Language,
    Items: [],
  },
  [PreferencesMenuTypeEnum.Accessibility]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.Accessibility,
    Items: [],
  },
  [PreferencesMenuTypeEnum.Interface]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.Interface,
    Items: [],
  },
};

// TODO: Future implementation
// const selectNotificationsOptions = [
//   'De todas as notificações',
//   'Nenhuma notificação'
// ]

const selectPeriodicityOptions: IOptionData[] = [
  { description: 'Diário', label: 'Diário', value: 'Daily' },
  { description: 'Semanal', label: 'Semanal', value: 'Weekly' },
];

const selectFilesOptions: IOptionData[] = [
  { description: 'De todas as pastas', label: 'De todas as pastas', value: 'AllFolders' },
  { description: 'De pastas seguidas', label: 'De pastas seguidas', value: 'FollowedFolders' },
  { description: 'De disciplinas seguidas', label: 'De disciplinas seguidas', value: 'FollowedDisciplines' },
  { description: 'Nenhuma notificação', label: 'Nenhuma notificação', value: 'None' },
];

const selectActivitiesOptions: IOptionData[] = [
  { description: 'De todas as atividades', label: 'De todas as atividades', value: 'All' },
  { description: 'De atividades seguidas', label: 'De atividades seguidas', value: 'Followed' },
  { description: 'Nenhuma notificação', label: 'Nenhuma notificação', value: 'None' },
];

// TODO: Future implementation
// const selectFullActivitiesOptions: IOptionData[] = [
//   { description: 'De todas as atividades', label: 'De todas as atividades', value: 'All' },
//   { description: 'De atividades seguidas', label: 'De atividades seguidas', value: 'Followed' },
//   { description: 'Apenas menções', label: 'Apenas menções', value: 'Mentions' },
//   { description: 'Nenhuma notificação', label: 'Nenhuma notificação', value: 'None' }
// ]

const selectDisciplinesOptions: IOptionData[] = [
  { description: 'De todas as disciplinas', label: 'De todas as disciplinas', value: 'All' },
  { description: 'De disciplinas seguidas', label: 'De disciplinas seguidas', value: 'Followed' },
  { description: 'Nenhuma notificação', label: 'Nenhuma notificação', value: 'None' },
];

const selectDisciplinesOptionsCreated: IOptionData[] = [
  { description: 'De todas as disciplinas', label: 'De todas as disciplinas', value: 'All' },
  { description: 'Nenhuma notificação', label: 'Nenhuma notificação', value: 'None' },
];

const selectPlotagensOptions: IOptionData[] = [
  { description: 'De todas as plotagens', label: 'De todas as plotagens', value: 'All' },
  { description: 'Criadas por mim', label: 'Criadas por mim', value: 'CreatedByMe' },
  { description: 'Nenhuma notificação', label: 'Nenhuma notificação', value: 'None' },
];

const selectPlotagensOptionsCreatedByMe: IOptionData[] = [
  { description: 'De todas as plotagens', label: 'De todas as plotagens', value: 'All' },
  { description: 'Nenhuma notificação', label: 'Nenhuma notificação', value: 'None' },
];

// TODO: Future implementation
// const selectConstructionSitesOptions = [
//   'De todas as obras',
//   'Nenhuma notificação'
// ]

export type TypeSelectNotificationActionOptions = Record<TypeSelectNotificationOptionsEnum, IOptionData[]>;

export const hasSelectNotificationsOptions: TypeSelectNotificationActionOptions = {
  [NotificationActionEnum.FileCreated]: selectFilesOptions,
  [NotificationActionEnum.FileDeleted]: selectFilesOptions,
  [NotificationActionEnum.FileMoved]: selectFilesOptions,
  [NotificationActionEnum.FileCreatedObsolet]: selectFilesOptions,
  [NotificationActionEnum.FileRenamed]: selectFilesOptions,
  [NotificationActionEnum.FileVersionAdded]: selectFilesOptions,
  [NotificationActionEnum.FileDescriptionUpdated]: selectFilesOptions,
  [NotificationActionEnum.FileRendered]: selectFilesOptions,
  [NotificationActionEnum.FileStatusChanged]: selectFilesOptions,
  [NotificationActionEnum.FolderCreated]: selectFilesOptions,
  [NotificationActionEnum.FolderRenamed]: selectFilesOptions,
  [NotificationActionEnum.FolderDeleted]: selectFilesOptions,

  [NotificationActionEnum.TopicCreated]: selectActivitiesOptions,
  [NotificationActionEnum.TopicUpdated]: selectActivitiesOptions,
  [NotificationActionEnum.TopicStatusChanged]: selectActivitiesOptions,
  [NotificationActionEnum.TopicExpiredYesterday]: selectActivitiesOptions,
  [NotificationActionEnum.TopicExpiresTommorow]: selectActivitiesOptions,
  [NotificationActionEnum.TopicExpiresToday]: selectActivitiesOptions,
  [NotificationActionEnum.TopicDeleted]: selectActivitiesOptions,
  [NotificationActionEnum.TopicAttachmentAdded]: selectActivitiesOptions,
  [NotificationActionEnum.TopicAttachmentDeleted]: selectActivitiesOptions,
  [NotificationActionEnum.TopicCommentAdded]: selectActivitiesOptions,

  [NotificationActionEnum.PlottingCreated]: selectPlotagensOptionsCreatedByMe,
  [NotificationActionEnum.PlottingStatusChanged]: selectPlotagensOptions,
  [NotificationActionEnum.PlottingFileDeleted]: selectPlotagensOptions,
  [NotificationActionEnum.PlottingRequestDeleted]: selectPlotagensOptions,
  [NotificationActionEnum.PlottingCreatedObsolet]: selectPlotagensOptions,

  [NotificationActionEnum.DisciplineCreated]: selectDisciplinesOptionsCreated,
  [NotificationActionEnum.DisciplineDeleted]: selectDisciplinesOptions,
  [NotificationActionEnum.DisciplineRenamed]: selectDisciplinesOptions,
  [NotificationActionEnum.DisciplineDisconnected]: selectDisciplinesOptions,
  [NotificationActionEnum.DisciplineReconnected]: selectDisciplinesOptions,

  [PreferencesActionEnum.Periodicity]: selectPeriodicityOptions,
};
